import React, { useState, useEffect } from 'react';
import './TalkFormModal.css';
import { useAuth } from '../../context/AuthContext';
import { createTalk } from '../../services/qoreTalksApi';
import { QORE_TALK_CATEGORIES } from '../../constants/qoreTalks';

const TalkFormModal = ({ isOpen, open, onClose, onSubmit, onCreated, talk = null, mode = 'create' }) => {
  const modalOpen = isOpen ?? open ?? false;
  const { token } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Technology',
    tags: [],
    duration: '',
    guests: [],
    showNotes: '',
    resources: [],
    status: 'draft',
    scheduledFor: '',
    isPremium: false,
    price: 0,
    season: {
      number: 1,
      episode: 1
    }
  });

  const [files, setFiles] = useState({
    audio: null,
    video: null,
    thumbnail: null,
    transcript: null
  });

  const [newTag, setNewTag] = useState('');
  const [newResource, setNewResource] = useState({ title: '', url: '', description: '' });

  const categories = QORE_TALK_CATEGORIES;

  useEffect(() => {
    if (talk && mode === 'edit') {
      setFormData({
        title: talk.title || '',
        description: talk.description || '',
        category: talk.category || 'Technology',
        tags: talk.tags || [],
        duration: talk.duration || '',
        guests: talk.guests || [],
        showNotes: talk.showNotes || '',
        resources: talk.resources || [],
        status: talk.status || 'draft',
        scheduledFor: talk.scheduledFor ? new Date(talk.scheduledFor).toISOString().slice(0, 16) : '',
        isPremium: talk.isPremium || false,
        price: talk.price || 0,
        season: talk.season || { number: 1, episode: 1 }
      });
    } else {
      // Reset form for create mode
      setFormData({
        title: '',
        description: '',
        category: 'Technology',
        tags: [],
        duration: '',
        guests: [],
        showNotes: '',
        resources: [],
        status: 'draft',
        scheduledFor: '',
        isPremium: false,
        price: 0,
        season: { number: 1, episode: 1 }
      });
    }
    setFiles({ audio: null, video: null, thumbnail: null, transcript: null });
  }, [talk, mode, modalOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleFileChange = (e) => {
    const { name, files: fileList } = e.target;
    setFiles(prev => ({
      ...prev,
      [name]: fileList[0]
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleAddResource = () => {
    if (newResource.title.trim() && newResource.url.trim()) {
      setFormData(prev => ({
        ...prev,
        resources: [...prev.resources, { ...newResource }]
      }));
      setNewResource({ title: '', url: '', description: '' });
    }
  };

  const handleRemoveResource = (index) => {
    setFormData(prev => ({
      ...prev,
      resources: prev.resources.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!files.audio && !files.video) {
      setSubmitError('Please upload an audio or video file.');
      return;
    }

    const submitData = new FormData();
    Object.keys(formData).forEach(key => {
      if (key === 'tags' || key === 'resources' || key === 'season') {
        submitData.append(key, JSON.stringify(formData[key]));
      } else if (formData[key] !== '' && formData[key] != null) {
        submitData.append(key, formData[key]);
      }
    });
    Object.keys(files).forEach(key => {
      if (files[key]) submitData.append(key, files[key]);
    });

    if (onSubmit) {
      onSubmit(submitData);
      return;
    }

    if (!token) {
      setSubmitError('You must be logged in as admin or instructor.');
      return;
    }

    setSubmitting(true);
    try {
      await createTalk(submitData, token);
      onCreated?.();
      onClose?.();
    } catch (err) {
      setSubmitError(err.message || 'Failed to save talk');
    } finally {
      setSubmitting(false);
    }
  };

  if (!modalOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content talk-form-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{mode === 'create' ? 'Create New QoreTalk' : 'Edit QoreTalk'}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <form className="talk-form" onSubmit={handleSubmit}>
          <div className="form-sections">
            {/* Basic Information */}
            <div className="form-section">
              <h4>Basic Information</h4>
              
              <div className="form-group">
                <label htmlFor="title">Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter talk title"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description *</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  rows="4"
                  placeholder="Describe your talk"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="category">Category *</label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="duration">Duration (minutes) *</label>
                  <input
                    type="number"
                    id="duration"
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    required
                    min="1"
                    placeholder="e.g., 45"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="season.number">Season</label>
                  <input
                    type="number"
                    id="season.number"
                    name="season.number"
                    value={formData.season.number}
                    onChange={handleInputChange}
                    min="1"
                    placeholder="1"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="season.episode">Episode</label>
                  <input
                    type="number"
                    id="season.episode"
                    name="season.episode"
                    value={formData.season.episode}
                    onChange={handleInputChange}
                    min="1"
                    placeholder="1"
                  />
                </div>
              </div>
            </div>

            {/* Media Files */}
            <div className="form-section">
              <h4>Media Files</h4>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="audio">Audio File *</label>
                  <input
                    type="file"
                    id="audio"
                    name="audio"
                    accept="audio/*"
                    onChange={handleFileChange}
                    className="file-input"
                  />
                  <div className="file-info">
                    {files.audio ? files.audio.name : 'No file selected'}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="video">Video File (Optional)</label>
                  <input
                    type="file"
                    id="video"
                    name="video"
                    accept="video/*"
                    onChange={handleFileChange}
                    className="file-input"
                  />
                  <div className="file-info">
                    {files.video ? files.video.name : 'No file selected'}
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="thumbnail">Thumbnail Image</label>
                  <input
                    type="file"
                    id="thumbnail"
                    name="thumbnail"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="file-input"
                  />
                  <div className="file-info">
                    {files.thumbnail ? files.thumbnail.name : 'No file selected'}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="transcript">Transcript File</label>
                  <input
                    type="file"
                    id="transcript"
                    name="transcript"
                    accept=".txt,.pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="file-input"
                  />
                  <div className="file-info">
                    {files.transcript ? files.transcript.name : 'No file selected'}
                  </div>
                </div>
              </div>
            </div>

            {/* Tags */}
            <div className="form-section">
              <h4>Tags</h4>
              
              <div className="tags-input">
                <div className="tag-input-group">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add a tag"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  />
                  <button type="button" onClick={handleAddTag} className="btn btn-outline">
                    Add Tag
                  </button>
                </div>
                
                <div className="tags-display">
                  {formData.tags.map((tag, index) => (
                    <span key={index} className="tag">
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="tag-remove"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Show Notes */}
            <div className="form-section">
              <h4>Show Notes</h4>
              
              <div className="form-group">
                <textarea
                  name="showNotes"
                  value={formData.showNotes}
                  onChange={handleInputChange}
                  rows="6"
                  placeholder="Add detailed show notes, key points, timestamps, etc."
                />
              </div>
            </div>

            {/* Resources */}
            <div className="form-section">
              <h4>Resources</h4>
              
              <div className="resource-input">
                <div className="form-row">
                  <div className="form-group">
                    <input
                      type="text"
                      value={newResource.title}
                      onChange={(e) => setNewResource(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Resource title"
                    />
                  </div>
                  <div className="form-group">
                    <input
                      type="url"
                      value={newResource.url}
                      onChange={(e) => setNewResource(prev => ({ ...prev, url: e.target.value }))}
                      placeholder="Resource URL"
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <input
                    type="text"
                    value={newResource.description}
                    onChange={(e) => setNewResource(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Resource description"
                  />
                </div>
                
                <button type="button" onClick={handleAddResource} className="btn btn-outline">
                  Add Resource
                </button>
              </div>
              
              <div className="resources-display">
                {formData.resources.map((resource, index) => (
                  <div key={index} className="resource-item">
                    <div className="resource-content">
                      <h5>{resource.title}</h5>
                      <p>{resource.url}</p>
                      {resource.description && <p className="resource-desc">{resource.description}</p>}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveResource(index)}
                      className="btn btn-danger btn-small"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Publishing Options */}
            <div className="form-section">
              <h4>Publishing Options</h4>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="status">Status</label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="draft">Draft</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="published">Published</option>
                  </select>
                </div>

                {formData.status === 'scheduled' && (
                  <div className="form-group">
                    <label htmlFor="scheduledFor">Schedule For</label>
                    <input
                      type="datetime-local"
                      id="scheduledFor"
                      name="scheduledFor"
                      value={formData.scheduledFor}
                      onChange={handleInputChange}
                    />
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="isPremium"
                      checked={formData.isPremium}
                      onChange={handleInputChange}
                    />
                    Premium Content
                  </label>
                </div>

                {formData.isPremium && (
                  <div className="form-group">
                    <label htmlFor="price">Price ($)</label>
                    <input
                      type="number"
                      id="price"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn btn-outline">
              Cancel
            </button>
            {submitError && <p className="form-error" style={{ color: '#f87171' }}>{submitError}</p>}
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Saving…' : (mode === 'create' ? 'Create Talk' : 'Update Talk')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TalkFormModal;
